import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  Alert,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Description as FormIcon,
  Person as PersonIcon,
  CalendarToday as DateIcon,
  Category as CategoryIcon,
  CheckCircle as StatusIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import clientsApi from '../../api/clientsApi';

const FormDetail = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFormData = async () => {
      setLoading(true);
      try {
        // Fetch form submission details
        const response = await clientsApi.getFormSubmission(formId);
        setFormData(response);
        setError(null);
      } catch (err) {
        console.error('Error fetching form details:', err);
        setError('Failed to load form details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (formId) {
      fetchFormData();
    }
  }, [formId]);

  const handleBack = () => {
    navigate('/forms');
  };

  const handleViewClient = () => {
    if (formData?.client_id) {
      navigate(`/clients/${formData.client_id}`);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return '';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'questionnaire':
        return 'primary';
      case 'consent':
        return 'secondary';
      case 'treatment':
        return 'info';
      case 'lab':
        return 'warning';
      default:
        return 'default';
    }
  };

  const renderFormResponse = (response) => {
    if (!response) return null;

    // Try to parse string responses
    let parsedResponse = response;
    if (typeof response === 'string') {
      try {
        parsedResponse = JSON.parse(response);
      } catch (e) {
        return (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {response}
          </Typography>
        );
      }
    }

    // If we have standard answers array, render like client modal
    if (parsedResponse && typeof parsedResponse === 'object' && Array.isArray(parsedResponse.answers)) {
      return (
        <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
          {parsedResponse.answers.map((answer, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              {answer.question && (
                <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
                  {answer.question}
                </Typography>
              )}
              {answer.answer && (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {String(answer.answer)}
                </Typography>
              )}
              {answer.files && Array.isArray(answer.files) && answer.files.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Files: {answer.files.length} attached
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Paper>
      );
    }

    // Generic object rendering as a fallback (avoid raw JSON where possible)
    if (typeof parsedResponse === 'object' && parsedResponse !== null) {
      // If the object looks like a flat key-value set, render neatly
      const entries = Object.entries(parsedResponse);
      const shouldPrettyList = entries.every(([_, v]) => typeof v !== 'object' || v === null);
      if (shouldPrettyList) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {entries.map(([key, value]) => (
              <Box key={key}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Typography>
                <Typography variant="body1" sx={{ ml: 2, whiteSpace: 'pre-wrap' }}>
                  {String(value)}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      }
      // As a last resort, pretty JSON
      return (
        <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(parsedResponse, null, 2)}
          </Typography>
        </Paper>
      );
    }

    return (
      <Typography variant="body2">
        {String(parsedResponse)}
      </Typography>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Forms
        </Button>
      </Box>
    );
  }

  if (!formData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Form submission not found.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Forms
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/forms')}
          underline="hover"
          color="inherit"
        >
          Forms
        </Link>
        <Typography color="text.primary" variant="body2">
          {formData.form_name || 'Form Submission'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FormIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4">
              {formData.form_name || 'Form Submission'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {formData.form_category && (
              <Chip
                icon={<CategoryIcon />}
                label={formData.form_category}
                color={getCategoryColor(formData.form_category)}
                size="small"
              />
            )}
            <Chip
              icon={<StatusIcon />}
              label={formData.status || 'Unknown'}
              color={getStatusColor(formData.status)}
              size="small"
            />
            {formData.submitted_date && (
              <Chip
                icon={<DateIcon />}
                label={formatTimeAgo(formData.submitted_date)}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Forms
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Form Information Card */}
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Form Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Form Name
                  </Typography>
                  <Typography variant="body1">
                    {formData.form_name || 'Unknown'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body1">
                    {formData.form_category || 'Not specified'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1">
                    {formData.status || 'Unknown'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Submitted Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(formData.submitted_date)}
                  </Typography>
                </Box>

                {formData.created_date && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Created Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTime(formData.created_date)}
                    </Typography>
                  </Box>
                )}

                {formData.updated_date && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTime(formData.updated_date)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Client Information Card */}
          {formData.client_name && (
            <Card elevation={2} sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Client Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon color="action" />
                  <Typography variant="body1">
                    {formData.client_name}
                  </Typography>
                </Box>

                {formData.client_email && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {formData.client_email}
                    </Typography>
                  </Box>
                )}

                {formData.client_phone && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">
                      {formData.client_phone}
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleViewClient}
                  startIcon={<PersonIcon />}
                >
                  View Client Profile
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Form Responses Card */}
        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Form Responses
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {formData.form_data ? (
                <Box sx={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {renderFormResponse(formData.form_data)}
                </Box>
              ) : (
                <Alert severity="info">
                  No form responses available for this submission.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Additional Information */}
          {formData.notes && (
            <Card elevation={2} sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {formData.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default FormDetail;